// --- Pagination.tsx ---
import React from "react";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onChangePage: (page: number) => void;
  hasDataOnLastPage: boolean;
  testId?:string
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onChangePage,
  hasDataOnLastPage,
  testId
}) => {

  // 🔥 Adjusted to prevent invalid pages
  const adjustedCurrentPage = Math.min(currentPage, totalPages - 1);

  const handlePageChange = (page: number) => {
    if (
      page >= 0 &&
      page < totalPages &&
      page !== adjustedCurrentPage
    ) {
      onChangePage(page);
    }
  };

  const renderPageNumbers = () => {
    if (totalPages <= 1) return null;

    const pages: React.ReactNode[] = [];
    const showLeft = adjustedCurrentPage > 2;
    const showRight = adjustedCurrentPage < totalPages - 3;

    const start = showLeft ? adjustedCurrentPage - 1 : 1;
    const end = showRight ? adjustedCurrentPage + 1 : totalPages - 2;

    pages.push(
      <button
        key={0}
        onClick={() => handlePageChange(0)}
        className={`mx-0.5 text-sm py-2 px-4 rounded-md ${
          adjustedCurrentPage === 0 ? "border border-[#B60F8D] text-[#B60F8D]" : ""
        }`}
      >
        1
      </button>
    );

    if (showLeft) pages.push(<span key="left" className="mx-1 text-gray-500">...</span>);

    for (let page = start; page <= end; page++) {
      pages.push(
        <button
          key={page}
          onClick={() => handlePageChange(page)}
          className={`mx-0.5 text-sm py-2 px-4 rounded-md ${
            adjustedCurrentPage === page ? "border border-[#B60F8D] text-[#B60F8D]" : ""
          }`}
        >
          {page + 1}
        </button>
      );
    }

    if (showRight) pages.push(<span key="right" className="mx-1 text-gray-500">...</span>);

    pages.push(
      <button
        key={totalPages - 1}
        onClick={() => handlePageChange(totalPages - 1)}
        className={`mx-0.5 text-sm py-2 px-4 rounded-md ${
          adjustedCurrentPage === totalPages - 1 ? "border border-[#B60F8D] text-[#B60F8D]" : ""
        }`}
      >
        {totalPages}
      </button>
    );

    return pages;
  };

  return (
    <nav className="px-5 py-5" aria-label="Pagination" data-test-id={testId}>
      <div className="inline-flex">

        <button
          onClick={() => handlePageChange(0)}
          disabled={adjustedCurrentPage === 0}
          className={`px-4 ${adjustedCurrentPage === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <SkipPreviousIcon />
        </button>

        <button
          onClick={() => handlePageChange(adjustedCurrentPage - 1)}
          disabled={adjustedCurrentPage === 0}
          className={`px-4 ${adjustedCurrentPage === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <PlayArrowIcon sx={{ transform: "rotate(180deg)" }} />
        </button>

        {renderPageNumbers()}

        <button
          onClick={() => handlePageChange(adjustedCurrentPage + 1)}
          disabled={adjustedCurrentPage === totalPages - 1}
          className={`px-4 ${adjustedCurrentPage === totalPages - 1 ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <PlayArrowIcon />
        </button>

        <button
          onClick={() => handlePageChange(totalPages - 1)}
          disabled={adjustedCurrentPage === totalPages - 1}
          className={`px-4 ${adjustedCurrentPage === totalPages - 1 ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <SkipNextIcon />
        </button>
      </div>
    </nav>
  );
};
